import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { CommandPalette } from "@/components/CommandPalette";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { useGlobalShortcuts } from "@/lib/shortcuts";
import { ShortcutsCheatsheet } from "@/components/ShortcutsCheatsheet";

/**
 * Sectioned navigation. Each domain (Movies / TV / Music) gets its
 * own group with sub-pages, instead of a flat 18-link list.
 *
 * Active state is communicated three ways for redundancy:
 *   1. brass left bar (2px)
 *   2. brass-tinted background
 *   3. ivory text (vs dim default)
 */

interface NavLink {
  to: string;
  label: string;
  /** Display-only keyboard hint shown on the right of the link. */
  shortcut?: string;
}

interface NavSection {
  label: string;
  /** Optional small glyph next to the section header. */
  glyph?: "library" | "system";
  links: NavLink[];
}

const sections: NavSection[] = [
  {
    label: "Overview",
    links: [
      { to: "/", label: "Dashboard", shortcut: "g h" },
      { to: "/search", label: "Search", shortcut: "/" },
      { to: "/search/saved", label: "Saved searches" },
      { to: "/calendar", label: "Calendar" },
      { to: "/health", label: "Health" },
      { to: "/activity", label: "Activity" },
    ],
  },
  {
    label: "Movies",
    glyph: "library",
    links: [
      { to: "/movies", label: "Library", shortcut: "g m" },
      { to: "/movies/wanted", label: "Wanted" },
      { to: "/movies/collections", label: "Collections" },
      { to: "/movies/import-lists", label: "Import lists" },
    ],
  },
  {
    label: "TV",
    glyph: "library",
    links: [
      { to: "/tv", label: "Library", shortcut: "g t" },
      { to: "/tv/wanted", label: "Wanted" },
      { to: "/tv/import-lists", label: "Import lists" },
    ],
  },
  {
    label: "Music",
    glyph: "library",
    links: [
      { to: "/music", label: "Artists", shortcut: "g a" },
      { to: "/music/wanted", label: "Wanted" },
      { to: "/music/playlists", label: "Playlists" },
      { to: "/music/discover", label: "Discover" },
      { to: "/music/integrity", label: "Integrity" },
      { to: "/music/playlist-import", label: "Playlist import" },
    ],
  },
  {
    label: "Subtitles",
    glyph: "library",
    links: [{ to: "/subtitles/wanted", label: "Wanted" }],
  },
  {
    label: "NZB",
    glyph: "system",
    links: [
      { to: "/nzb/queue", label: "Queue" },
      { to: "/nzb/history", label: "History" },
    ],
  },
  {
    label: "Settings",
    glyph: "system",
    links: [
      { to: "/settings/quality", label: "Quality" },
      { to: "/settings/indexers", label: "Indexers" },
      { to: "/settings/tags", label: "Tags" },
      { to: "/settings/download-clients", label: "Download Clients" },
      { to: "/settings/path-mappings", label: "Path Mappings" },
      { to: "/settings/movies", label: "Movies" },
      { to: "/settings/tv", label: "TV" },
      { to: "/settings/music", label: "Music" },
      { to: "/settings/tmdb", label: "TMDB" },
      { to: "/settings/spotify", label: "Spotify" },
      { to: "/settings/nzb", label: "NZB" },
      { to: "/settings/subtitles", label: "Subtitles" },
      { to: "/settings/notifications", label: "Notifications" },
      { to: "/settings/library-permissions", label: "Library Permissions" },
      { to: "/settings/mcp-tokens", label: "MCP Tokens" },
      { to: "/settings/backups", label: "Backups" },
      { to: "/settings/system", label: "System" },
      { to: "/settings/bulk-redownload", label: "Bulk Redownload" },
    ],
  },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  // Mobile drawer state. The sidebar is permanently visible on
  // viewports ≥ md; below that it slides over the content from the
  // left, with a backdrop. Closes on route change or backdrop tap.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useGlobalShortcuts({
    onOpenPalette: () => setPaletteOpen(true),
    onOpenCheatsheet: () => setCheatsheetOpen(true),
  });

  // Auto-close the drawer when the user navigates. Without this the
  // drawer stays pinned over the new page after a link tap, which
  // feels broken on mobile.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // Lock body scroll while the drawer is open so the page underneath
  // doesn't scroll when the user gestures inside the drawer.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  // Close on Escape — matches the modal pattern users already know.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setMobileNavOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  return (
    <div className="flex h-full flex-col md:grid md:grid-cols-[17rem_1fr]">
      {/* Mobile top bar — visible only < md. Hosts the hamburger,
          brand, and the command-palette icon so the user has a way
          to navigate without seeing the full sidebar. */}
      <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-surface/85 px-4 py-3 backdrop-blur-md md:hidden">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation"
          aria-expanded={mobileNavOpen}
          className="focus-ring grid h-9 w-9 place-items-center rounded-md border border-line text-foreground/75 transition hover:border-line-strong hover:text-foreground"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            aria-hidden
          >
            <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
          </svg>
        </button>
        <Mark small />
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold leading-none tracking-display-tight">
            arr-in-one
          </div>
        </div>
        <button
          onClick={() => setPaletteOpen(true)}
          aria-label="Open command palette"
          className="focus-ring grid h-9 w-9 place-items-center rounded-md border border-line text-foreground/55 transition hover:border-line-strong hover:text-foreground"
          title="Command palette"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.4}
            aria-hidden
          >
            <circle cx={7} cy={7} r={4.5} />
            <path d="m13 13-2.7-2.7" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Mobile drawer backdrop — separate element so the click
          handler stays isolated from the panel. */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          aria-hidden
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-line bg-surface/95 backdrop-blur-md transition-transform duration-200 ease-out md:static md:z-auto md:w-auto md:max-w-none md:transform-none md:bg-surface/40",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
        aria-label="Primary navigation"
      >
        {/* Brand — desktop only; mobile uses the top bar above. */}
        <div className="hidden items-center gap-3 border-b border-line px-5 py-4 md:flex">
          <Mark />
          <div className="min-w-0 flex-1">
            <div className="font-display text-base font-semibold leading-none tracking-display-tight">
              arr-in-one
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/40">
              v0.1.0
            </div>
          </div>
          <button
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
            className="focus-ring grid h-7 w-7 place-items-center rounded-md border border-line text-foreground/55 transition hover:border-line-strong hover:text-foreground"
            title="Command palette (⌘K)"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.4}
              aria-hidden
            >
              <circle cx={7} cy={7} r={4.5} />
              <path d="m13 13-2.7-2.7" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Drawer header on mobile — close button + brand. */}
        <div className="flex items-center justify-between border-b border-line px-4 py-3 md:hidden">
          <div className="flex items-center gap-3">
            <Mark small />
            <div className="font-display text-sm font-semibold leading-none tracking-display-tight">
              arr-in-one
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation"
            className="focus-ring grid h-8 w-8 place-items-center rounded-md text-foreground/55 transition hover:bg-surface-2 hover:text-foreground"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-5">
            {sections.map((section) => (
              <Section
                key={section.label}
                section={section}
                pathname={pathname}
              />
            ))}
          </div>
        </nav>

        {/* User footer */}
        <div className="border-t border-line p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="truncate text-sm text-foreground/85">
              {user?.username}
            </span>
            <span className="rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/60">
              {user?.role}
            </span>
          </div>
          <button
            onClick={logout}
            className="focus-ring w-full rounded-md border border-line bg-surface-2/40 px-2.5 py-1.5 text-xs text-foreground/75 transition hover:border-line-strong hover:bg-surface-2 hover:text-foreground"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto md:flex-none">
        <div className="page-stage mx-auto max-w-7xl px-4 py-6 sm:px-6 md:px-8 md:py-8">
          <Outlet />
        </div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutsCheatsheet
        open={cheatsheetOpen}
        onClose={() => setCheatsheetOpen(false)}
      />
      {/* First-run onboarding wizard. Auto-opens when
          `/api/v1/onboarding/status` reports `needs_setup=true`; once
          stamped (via Skip or Done) it stays out of the way. */}
      <OnboardingWizard />
    </div>
  );
}

function Section({
  section,
  pathname,
}: {
  section: NavSection;
  pathname: string;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between px-2">
        <span className="font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-foreground/40">
          {section.label}
        </span>
      </div>
      <div className="space-y-px">
        {section.links.map((item) => {
          const active =
            item.to === "/"
              ? pathname === "/"
              : pathname === item.to || pathname.startsWith(`${item.to}/`);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "focus-ring group relative flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-sm transition",
                active
                  ? "bg-accent/10 text-foreground"
                  : "text-foreground/65 hover:bg-surface-2/50 hover:text-foreground",
              )}
            >
              {/* Active bar */}
              <span
                aria-hidden
                className={cn(
                  "absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-r bg-accent transition-opacity",
                  active ? "opacity-100" : "opacity-0",
                )}
              />
              <span className="ml-2 truncate">{item.label}</span>
              {item.shortcut && (
                <span className="hidden font-mono text-[10px] text-foreground/35 group-hover:inline xl:inline">
                  {item.shortcut}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Mark({ small = false }: { small?: boolean } = {}) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={cn("shrink-0", small ? "h-6 w-6" : "h-8 w-8")}
      aria-hidden
    >
      <circle
        cx={16}
        cy={16}
        r={14}
        fill="hsl(28 12% 7%)"
        stroke="hsl(38 56% 62%)"
        strokeWidth={1.5}
      />
      <path
        d="M11 22V11h7c2.5 0 4 1.5 4 4s-1.5 4-4 4h-7"
        fill="none"
        stroke="hsl(38 56% 62%)"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 19l5 4"
        fill="none"
        stroke="hsl(38 56% 62%)"
        strokeWidth={1.8}
        strokeLinecap="round"
      />
    </svg>
  );
}
